<template>
  <div
    class="mb-2 border-t-0 text-neutral-1 border-neutral-1"
    :class="type"
  >
    <div>
      <div class="flex flex-col md:space-x-6 md:flex-row md:items-center">
        <h3 class="text-3xl font-medium md:text-4xl">
          {{ type === "toSign" ? t("request.toSign") : t("request.toView") }}
        </h3>
      </div>
      
      <div class="space-x-3">
        <div
          v-if="type === 'toSign'"
          class="flex mt-1.5 "
          :class="docsPending(documents).length ? 'text-yellow-3' : 'text-green-3'"
        >
          <div
            class="px-2 text-base font-medium rounded-lg"
            :class="docsPending(documents).length ? 'bg-yellow-9' : 'bg-green-9'"
          >
            {{ docsToSign(documents).length - docsPending(documents).length }}/{{ docsToSign(documents).length }}
            {{ $t('base.done') }}
          </div>
        </div>

        <div
          v-if="type === 'toSign' && docsPending(documents).length && !!request.deadline_at"
          class="font-medium text-base rounded-lg px-2 bg-primary-10 text-primary-3 flex mt-1.5"
        >
          {{ formattedDeadline }}
        </div>
      </div>

      <div
        class="mt-6 md:-mr-12"
        :class="documents.length > 2 ? 'grid grid-cols-2' : 'flex flex-col md:flex-row'"
      >
        <DocumentCard
          v-for="document in sortedDocuments"
          :key="document.id"
          :class="documents.length > 2 ? 'col-span-2 md:col-span-1' : 'md:mr-7'"
          :document="document"
          :recipient="request.recipient"
          :request-id="request.id"
          :type="type"
          :previous-signing-completed="previousSigningCompleted(document)"
          :is-recipient-pending="
            !isRequestDeclined &&
              !!docsPending(documents) &&
              !!docsPending(documents).find(el => el.id === document.id)
          "
          :are-recipients-pending="
            !isRequestDeclined &&
              !!docsRecipientsPending(documents) &&
              !!docsRecipientsPending(documents).find(el => el.id === document.id)
          "
        />
      </div>
    </div>

    <div v-if="docsPending(documents).length == 0 && theme.goodVibesMessage === true">
      <p class="leading-6 mb-0.5 mt-2 rtl:text-right text-lg text-neutral-2">
        Sie haben die Identifikation sowie die digitalen Unterschriften auf Ihren Dokumenten nun erfolgreich abgeschlossen. Sie erhalten in Kürze von Biogena Good Vibes AG eine Bestätigung per E-Mail, dass Sie die zwei Ausfertigungen Ihres Zeichnungsscheins erfolgreich mittels „Qualifizierter Elektronischer Signatur“ unterschrieben haben.
      </p>  

      <p class="leading-6 mb-0.5 mt-2 rtl:text-right text-lg text-neutral-2">
        Sie können jetzt Ihre Dokumente jeweils separat herunterladen (nach dem Herunterladen jedes Dokuments auf „zurück“ klicken und Sie kommen wieder auf diese Seite).
      </p>  

      <p class="leading-6 mb-0.5 mt-4 rtl:text-right text-lg text-neutral-2">
        Klicken Sie danach auf <a class="underline" href="https://biogena.com/goodvibesinvest">Weiter</a>, um zur Website https://biogena.com/goodvibesinvest zurückzukehren.
      </p>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import useTheme from "../../composables/LayoutAndStyle/useTheme";
import DocumentCard from "./DocumentCard.vue";
import useDocuments from "../../composables/Request/useDocuments";
import { formatRelativeTime } from "../../functions/helpers";

export default {
  components: {
    DocumentCard,
  },
  props: {
    request: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      default: "toSign",
    },
    deadline: {
      type: String,
      default: undefined,
    },
    documents: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const { theme } = useTheme();
    const { docsPending, docsToSign, docsRecipientsPending, isRequestDeclined, isDocPending } = useDocuments(
      props.request.recipient.id, props.request
    );

    /* sort documents array (pendingDocs first) */
    const sortedDocuments = computed(() => {
      let tempDocuments = props.documents;
      return tempDocuments.sort((a, b) => {
        if (isDocPending(a) && !isDocPending(b)) {
          return -1;
        } else if (!isDocPending(a) && isDocPending(b)) {
          return 1;
        } else {
          return 0;
        }
      })
    });

    const formattedDeadline = computed(() => {
      return t("request.deadLine", {
        time: capitalizeFirstLetter(
          formatRelativeTime(props.request.deadline_at, 'de')
        ),
      });
    });

    function capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    }

    function previousSigningCompleted(document) {
      if (props.documents.filter(d => d.signing_order == null).length > 0) {
        return true;
      }

      if (document.signing_order === 1) {
        return true;
      }

      const previousSigningOrder = document.signing_order - 1;

      const previousDocuments = props.documents.filter(d => d.signing_order === previousSigningOrder);

      return previousDocuments.filter(d => isDocPending(d)).length === 0;
    }

    return {
      t,
      theme,
      isRequestDeclined,
      docsPending,
      docsToSign,
      docsRecipientsPending,
      formattedDeadline,
      sortedDocuments,
      previousSigningCompleted,
    };
  },
};
</script>
